/**
 * Structural metrics for the summary strip on the org chart PDF export.
 *
 * Computed from the same flat node array the chart itself was handed, so the
 * figures always describe the chart as drawn (type filters included) rather
 * than the unfiltered tree behind it.
 *
 * Definitions, worked against a two-tier chart of two roots with three and two
 * direct reports (7 positions, 2 layers, 2.5 average span, 3 maximum span):
 *
 *   positions    node count
 *   layers       deepest reporting level, roots counting as layer 1
 *   averageSpan  total children ÷ number of nodes with at least one child
 *   maximumSpan  largest direct-child count on any single node
 *   fte          sum of the per-position `fte` the API sends
 *
 * FTE is the one figure here that is not structural, and it is deliberately
 * only SUMMED. The formula — how much of the event's life a role is active for
 * — belongs to PositionMetricsService, which rounds each position to 2dp
 * precisely so that a column of them foots to its own total. Re-deriving it
 * here from the dates on the cards would put a second implementation in front
 * of the same user.
 *
 * Stub nodes — the "N hidden roles" placeholders `useOrganigramFilter` injects
 * for filtered-out types — are dropped first: they stand for positions that are
 * not on the chart, so counting them would inflate every figure.
 */

/** A node's parent id, normalised — OrgChart uses `''` for a root, the API may send null. */
function parentId(node) {
    const pid = node.pid;

    return pid === '' || pid === null || pid === undefined ? null : pid;
}

/**
 * @param {Array} nodes Flat OrgChart node array (the chart's own `nodes` prop).
 * @returns {{positions: number, layers: number, averageSpan: number, maximumSpan: number, fte: number}}
 */
export function organigramMetrics(nodes) {
    const positions = (Array.isArray(nodes) ? nodes : []).filter((node) => !node._isStub);

    if (positions.length === 0) {
        return { positions: 0, layers: 0, averageSpan: 0, maximumSpan: 0, fte: 0 };
    }

    const byId = new Map(positions.map((node) => [String(node.id), node]));

    // Children per parent. A pid pointing outside this set (a subtree fetched
    // without its parent, or a parent filtered out) makes the node a root here.
    const childrenOf = new Map();
    positions.forEach((node) => {
        const pid = parentId(node);
        const key = pid === null ? null : String(pid);

        if (key === null || !byId.has(key)) {
            return;
        }

        childrenOf.set(key, (childrenOf.get(key) ?? 0) + 1);
    });

    const spans = Array.from(childrenOf.values());
    const parentsWithChildren = spans.length;
    const totalChildren = spans.reduce((sum, count) => sum + count, 0);

    return {
        positions: positions.length,
        layers: deepestLayer(positions, byId),
        // One decimal place: the strip has room for "2.5", not "2.4285714".
        averageSpan: parentsWithChildren === 0 ? 0 : Math.round((totalChildren / parentsWithChildren) * 10) / 10,
        maximumSpan: spans.length === 0 ? 0 : Math.max(...spans),
        // Re-rounded to the 2dp the parts already carry — adding a few hundred
        // of them in binary floating point otherwise leaves a 0.00000001 tail.
        fte: Math.round(positions.reduce((total, node) => total + (Number(node.fte) || 0), 0) * 100) / 100,
    };
}

/**
 * Deepest reporting level, walking each node up to its root. Ancestors already
 * measured are reused, and a node revisited inside its own walk terminates it —
 * a cycle in `pid` would otherwise loop forever.
 */
function deepestLayer(positions, byId) {
    const depths = new Map();

    function depthOf(node) {
        const id = String(node.id);
        if (depths.has(id)) {
            return depths.get(id);
        }

        // Claim a provisional depth first so a cycle back to this node stops here.
        depths.set(id, 1);

        const pid = parentId(node);
        const parent = pid === null ? undefined : byId.get(String(pid));
        const depth = parent ? depthOf(parent) + 1 : 1;

        depths.set(id, depth);

        return depth;
    }

    return positions.reduce((deepest, node) => Math.max(deepest, depthOf(node)), 0);
}

/**
 * The type tags present on the chart, in `POSITION_TYPE_ORDER`, plus whether
 * any node carries the committed or behind-schedule mark — the PDF key lists
 * only what the reader can actually see, so a filtered export explains four
 * colours or one.
 *
 * @param {Array} nodes Flat OrgChart node array.
 * @returns {{types: Array<string>, untyped: boolean, committed: boolean, behindSchedule: boolean}}
 */
export function organigramLegendContents(nodes, typeOrder) {
    const positions = (Array.isArray(nodes) ? nodes : []).filter((node) => !node._isStub);
    const present = new Set(positions.map((node) => node.type));

    return {
        types: typeOrder.filter((type) => present.has(type)),
        untyped: positions.some((node) => !typeOrder.includes(node.type)),
        committed: positions.some((node) => Number(node.committed) > 0),
        behindSchedule: positions.some((node) => node.isBehindSchedule === true),
    };
}
